import React from 'react';
import {
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Slider,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CompactColorPicker from 'ui/components/CompactColorPicker/CompactColorPicker';
import { TableData } from '../../../types';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete';

interface TablePropertiesProps {
  tableProps: TableData;
  updateElementProperty: (key: string, value: any) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

const TableProperties = ({
  tableProps,
  updateElementProperty,
  handleKeyDown,
}: TablePropertiesProps) => {
  // Handler for stroke color changes
  const handleStrokeChange = (newBorderColor: string) => {
    const newTableDataState = {
      ...tableProps, // Use tableProps from the render scope
      borderColor: newBorderColor,
    };
    updateElementProperty('tableData', newTableDataState);
  };
  // Handler for rows changes
  const handleRowsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = Math.max(1, Math.min(20, Number(e.target.value)));
    updateElementProperty('tableData', { ...tableProps, rows: newRows });
  };

  // Handler for columns changes
  const handleColumnsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColumns = Math.max(1, Math.min(20, Number(e.target.value)));
    updateElementProperty('tableData', { ...tableProps, columns: newColumns });
  };

  // Handler for header text changes
  const handleHeaderTextChange = (index: number, value: string) => {
    const newHeaderTexts = [...(tableProps.headerTexts || [])];
    newHeaderTexts[index] = value;
    updateElementProperty('tableData', {
      ...tableProps,
      headerTexts: newHeaderTexts,
    });
  };

  const memoizedHeaderInputs = React.useMemo(() => {
    return Array.from({ length: tableProps.columns }).map((_, index) => (
      <TextField
        className="redesign"
        variant="standard"
        key={`header-text-${index}`}
        label={`Column ${index + 1}`}
        size="small"
        fullWidth
        value={tableProps.headerTexts?.[index] || `Header ${index + 1}`}
        onChange={(e) => handleHeaderTextChange(index, e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{ mb: 1 }}
      />
    ));
  }, [tableProps.columns, tableProps.headerTexts]);

  return (
    <>
      <Accordion square className="no-bottom-border" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
          <Typography variant="subtitle2">Table Structure</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <TextField
              className="redesign"
              variant="standard"
              label="Rows"
              size="small"
              type="number"
              value={tableProps.rows}
              onChange={handleRowsChange}
              onKeyDown={handleKeyDown}
              inputProps={{ min: 1, max: 20 }}
              sx={{ width: '50%' }}
            />
            <TextField
              className="redesign"
              variant="standard"
              label="Columns"
              size="small"
              type="number"
              value={tableProps.columns}
              onChange={handleColumnsChange}
              onKeyDown={handleKeyDown}
              inputProps={{ min: 1, max: 20 }}
              sx={{ width: '50%' }}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!tableProps.headerRow}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    updateElementProperty('tableData', {
                      ...tableProps,
                      headerRow: newValue,
                    });
                  }}
                  size="small"
                />
              }
              label="Header Row"
            />
          </Box>
        </AccordionDetails>
      </Accordion>

      {tableProps.headerRow && (
        <>
          <Accordion square className="no-bottom-border" defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
              <Typography variant="subtitle2">Header Content</Typography>
            </AccordionSummary>
            <AccordionDetails>{memoizedHeaderInputs}</AccordionDetails>
          </Accordion>

          <Accordion square className="no-bottom-border" defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
              <Typography variant="subtitle2">Header Styling</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mt: 2 }}>
                <TextField
                  className="redesign"
                  variant="standard"
                  label="Header Font Size"
                  size="small"
                  type="number"
                  value={tableProps.headerFontSize || 12}
                  onChange={(e) => {
                    const newValue = Math.max(
                      1,
                      Math.min(100, Number(e.target.value))
                    );
                    updateElementProperty('tableData', {
                      ...tableProps,
                      headerFontSize: newValue,
                    });
                  }}
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">pt</InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  label="Header Font Family"
                  value={tableProps.headerFontFamily || 'Helvetica'}
                  onChange={(e, newValue) => {
                    const value = newValue as string;
                    if (value) {
                      updateElementProperty('tableData', {
                        ...tableProps,
                        headerFontFamily: value,
                      });
                    }
                  }}
                  options={[
                    'Helvetica',
                    'Arial',
                    'Times New Roman',
                    'Courier',
                    'Georgia',
                    'Verdana',
                  ]}
                  getOptionLabel={(option) => option as string}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <CompactColorPicker
                  label="Header Background Color"
                  color={
                    tableProps.headerBackgroundColor === 'transparent'
                      ? '#ffffff'
                      : tableProps.headerBackgroundColor
                  }
                  onChange={(color) => {
                    if (tableProps.headerBackgroundColor !== 'transparent') {
                      updateElementProperty('tableData', {
                        ...tableProps,
                        headerBackgroundColor: color,
                      });
                    }
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        tableProps.headerBackgroundColor === 'transparent'
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const newColor = e.target.checked
                          ? 'transparent'
                          : tableProps.headerBackgroundColor === 'transparent'
                          ? '#ffffff'
                          : tableProps.headerBackgroundColor || '#ffffff';
                        updateElementProperty('tableData', {
                          ...tableProps,
                          headerBackgroundColor: newColor,
                        });
                      }}
                      size="small"
                    />
                  }
                  label="Transparent"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                <CompactColorPicker
                  label="Header Text Color"
                  color={tableProps.headerTextColor}
                  onChange={(color) => {
                    updateElementProperty('tableData', {
                      ...tableProps,
                      headerTextColor: color,
                    });
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      <Accordion square className="no-bottom-border" defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 0 }}>
          <Typography variant="subtitle2">Table Styling</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={tableProps.showGrid}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  updateElementProperty('tableData', {
                    ...tableProps,
                    showGrid: newValue,
                  });
                }}
                size="small"
              />
            }
            label="Show Grid Lines"
            sx={{ mb: 2 }}
          />

          <Box sx={{ mb: 2 }}>
            <CompactColorPicker
              label="Border Color"
              color={tableProps.borderColor || '#000000'}
              onChange={handleStrokeChange}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <CompactColorPicker
              label="Table Background Color"
              color={
                tableProps.backgroundColor === 'transparent'
                  ? '#ffffff'
                  : tableProps.backgroundColor
              }
              onChange={(color) => {
                if (tableProps.backgroundColor !== 'transparent') {
                  updateElementProperty('tableData', {
                    ...tableProps,
                    backgroundColor: color,
                  });
                }
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={tableProps.backgroundColor === 'transparent'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newColor = e.target.checked
                      ? 'transparent'
                      : tableProps.backgroundColor === 'transparent'
                      ? '#ffffff'
                      : tableProps.backgroundColor || '#ffffff';
                    updateElementProperty('tableData', {
                      ...tableProps,
                      backgroundColor: newColor,
                    });
                  }}
                  size="small"
                />
              }
              label="Transparent"
              sx={{ ml: 'auto' }}
            />
          </Box>
          <Box sx={{ mb: 2 }}>
            <CompactColorPicker
              label="Highlight Color"
              color={tableProps.highlightColor}
              onChange={(color) => {
                updateElementProperty('tableData', {
                  ...tableProps,
                  highlightColor: color,
                });
              }}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Border Width: {tableProps.borderWidth || 1}px
            </Typography>
            <Slider
              value={tableProps.borderWidth || 1}
              onChange={(event, newValue) => {
                const value =
                  typeof newValue === 'number' ? newValue : newValue[0];
                updateElementProperty('tableData', {
                  ...tableProps,
                  borderWidth: value,
                });
              }}
              min={1}
              max={10}
              valueLabelDisplay="auto"
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default TableProperties;
